import { create } from 'zustand';
import { businessApi } from '../api/businessApi';
import { logger } from '../utils/logger';
import type { Business } from '../types/business';

interface BusinessState {
  businesses: Business[];
  selectedBusiness: Business | null;
  isLoading: boolean;
  error: string | null;

  fetchBusinesses: () => Promise<Business[]>;
  selectBusiness: (business: Business) => void;
  clearSelection: () => void;
  updateSelectedBusiness: (updated: Business) => void;
  refreshSelectedBusiness: () => Promise<void>;
}

export const useBusinessStore = create<BusinessState>((set, get) => ({
  businesses: [],
  selectedBusiness: null,
  isLoading: false,
  error: null,

  fetchBusinesses: async () => {
    set({ isLoading: true, error: null });
    try {
      const businesses = await businessApi.getMyBusinesses();
      set({ businesses, isLoading: false });
      logger.info('Businesses loaded', { count: businesses.length });
      return businesses;
    } catch (err: unknown) {
      const error = err as { response?: { status?: number; data?: { message?: string; code?: string } } };
      const status = error?.response?.status;
      const message = error?.response?.data?.message || 'Bizneslarni yuklashda xatolik';
      const code = error?.response?.data?.code;
      set({ error: message, isLoading: false });
      logger.error('Failed to load businesses', { status, code, message });
      return [];
    }
  },

  selectBusiness: (business) => {
    set({ selectedBusiness: business });
    logger.info('Business selected', { id: business.id, name: business.businessName });
  },

  clearSelection: () => {
    set({ selectedBusiness: null });
  },

  updateSelectedBusiness: (updated) => {
    set((state) => ({
      selectedBusiness: updated,
      businesses: state.businesses.map((b) => (b.id === updated.id ? updated : b)),
    }));
  },

  refreshSelectedBusiness: async () => {
    const { selectedBusiness } = get();
    if (!selectedBusiness) return;
    try {
      const updated = await businessApi.getTenant(selectedBusiness.id);
      set((state) => ({
        selectedBusiness: updated,
        businesses: state.businesses.map((b) => (b.id === updated.id ? updated : b)),
      }));
    } catch {
      logger.error('Failed to refresh business');
    }
  },
}));
